import React from "react";

//material ui
import { useTheme } from "@mui/styles";
import { Box, Typography, useMediaQuery } from "@mui/material";

//components
import WantToApplyButton from "../Buttons/WantToApplyButton";

//img
import HomeBlueBackground from "../../img/HomeBlueBackground.png";
import SalesforceBackground from "../../img/SalesforceBackground.png";

//json2mq
import json2mq from "json2mq";
import { useLocation } from "react-router-dom";
import useTranslation from "../../hooks/useTranslation";
import { useContext } from "react";
import { ColorContext } from "../../pages/Bootcamp";

const useStyles = (theme) => ({
  box: {
    height: { xs: "533px", md: "600px", lg: "700px", xl: "850px" },
    width: "100%",
    backgroundImage: {
      xs: `url(${HomeBlueBackground})`,
      md: `url(${SalesforceBackground})`,
    },
    borderRadius: { xs: "0 0 40px 40px", md: "0px 0px 116px 116px" },
    backgroundRepeat: "no-repeat",
    backgroundSize: { xs: "cover", md: "100% 100%" },
    display: "flex",
    alignItems: { xs: "center", md: "flex-start" },
    flexDirection: { xs: "column", md: "row" },
    color: theme.palette.white.main,
  },
  title: {
    maxWidth: { xs: "277px", md: "400px", lg: "500px", xl: "597px" },
    textAlign: { xs: "center", md: "inherit" },
    fontFamily: theme.typography.montserrat.bold,
    fontSize: { xs: "35px", md: "45px", lg: "55px", xl: "70px" },
    lineHeight: { xs: "43px", md: "inherit" },
    mt: { xs: "62px", md: "75px", lg: "85px", xl: "100px" },
    ml: { md: "100px", lg: "150px", xl: "265px" },
  },
  span: { fontWeight: "bold" },
  subtitle: {
    width: { xs: "225px", md: "350px", lg: "450px", xl: "560px" },
    textAlign: { xs: "center", md: "start" },
    fontFamily: theme.typography.montserrat.regular,
    fontSize: { xs: "16px", md: "19px" },
    lineHeight: { xs: "26px", md: "30px" },
    height: { xs: "119px", md: "106px" },
    mt: { xs: "17px", md: "24px", lg: "34px", xl: "44px" },
    ml: { md: "100px", lg: "150px", xl: "265px" },
  },
  poweredByBox: {
    mt: { xs: "28px", md: 0 },
    ml: { md: "100px", lg: "150px", xl: "265px" },
  },
  poweredBy: {
    lineHeight: { xs: "44px" },
    fontSize: { xs: "14px", md: "16px", lg: "18px", xl: "20px" },
  },
  imgContainer: {
    display: "flex",
    alignItems: { xs: "center" },
    justifyContent: { xs: "space-between" },
    width: { xs: "240px", md: "260px" },
    mt: { xs: "16px", md: 0 },
    ml: { md: "20px", lg: "30px", xl: "43px" },
  },
  buttonContainer: {
    ml: { md: "100px", lg: "150px", xl: "265px" },
    mt: { md: "20px", lg: "30px", xl: "45px" },
  },
  poweredByContainer: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: { xs: "center" },
    mt: { md: "30px", lg: "20px", xl: "50px" },
  },
  firstChild: {
    display: "flex",
    alignItems: { xs: "center", md: "flex-start" },
    flexDirection: { xs: "column" },
  },
  imageBox: {
    mt: { md: "100px", xl: "120px" },
    ml: { md: "50px", lg: "80px", xl: "200px" },
  },
});

const BootcampTitle = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { pathname } = useLocation();
  const componentName = "BootcampTitle";
  const translation = useTranslation(pathname, componentName);

  const xs = useMediaQuery(
    json2mq({
      maxWidth: 899,
    })
  );
  const sm = useMediaQuery(
    json2mq({
      maxWidth: 1000,
      minWidth: 900,
    })
  );

  const md = useMediaQuery(
    json2mq({
      maxWidth: 1200,
      minWidth: 1000,
    })
  );

  const lg = useMediaQuery(
    json2mq({
      maxWidth: 1535,
      minWidth: 1201,
    })
  );
  const xl = useMediaQuery(
    json2mq({
      minWidth: 1536,
      maxWidth: 1700,
    })
  );

  const xxl = useMediaQuery(
    json2mq({
      minWidth: 1701,
    })
  );

  const stylesHelper = () => {
    if (xs) {
      return {
        cloudGaia: { width: "78px", height: "44px" },
        salesforcePartner: { width: "124.5px", height: "58.1px" },
      };
    } else if (xl) {
      return {
        cloudGaia: { width: "145px", height: "81px", marginRight: "32px" },
        salesforcePartner: { width: "229px", height: "107.3px" },
        mainImage: { width: "400px", height: "400px" },
      };
    } else if (sm)
      return {
        cloudGaia: { width: "78px", height: "44px" },
        salesforcePartner: { width: "124.5px", height: "58.1px" },
        mainImage: { width: "300px", height: "270px" },
      };
    else if (md) {
      return {
        cloudGaia: { width: "78px", height: "44px" },
        salesforcePartner: { width: "124.5px", height: "58.1px" },
        mainImage: { width: "350px", height: "350px" },
      };
    } else if (lg) {
      return {
        cloudGaia: { width: "110px", height: "60px" },
        salesforcePartner: { width: "120px", height: "70px" },
        mainImage: { width: "400px", height: "400px" },
      };
    } else if (xxl) {
      return {
        cloudGaia: { width: "145px", height: "81px", marginRight: "32px" },
        salesforcePartner: { width: "229px", height: "107.3px" },
        mainImage: { width: "500px", height: "500px" },
      };
    }
  };

  const isMobile = useMediaQuery("(max-width:899px)");
  const {
    title,
    subtitle,
    poweredBy,
    companyImg,
    partnerImg,
    backgroundLogoImg,
  } = translation;
  const { mainColor, secondaryColor, iconColor } = useContext(ColorContext);
  const bootcampName = pathname.split("/")[2];

  return (
    <Box sx={classes.box}>
      <Box sx={classes.firstChild}>
        <Typography sx={classes.title}>{title}</Typography>
        <Typography sx={classes.subtitle}>
          {subtitle[0]} {xs && <br />}
          {subtitle[1]}
          <span style={classes.span}>{subtitle[2]}</span>
        </Typography>

        <Box sx={classes.buttonContainer}>
          {bootcampName === "salesforce" ? null : (
            <WantToApplyButton
              mainColor={mainColor}
              secondaryColor={secondaryColor}
              iconColor={iconColor}
              mt={xs && "18px"}
              navigateTo={`/aplica/${bootcampName}`}
              height={{
                xs: "52px",
                sm: "42.25px",
                md: "46.5px",
                lg: "60px",
                xl: "64px",
              }}
            />
          )}
        </Box>

        <Box sx={classes.poweredByContainer}>
          <Box sx={classes.poweredByBox}>
            <Typography sx={classes.poweredBy}>{poweredBy}</Typography>
          </Box>
          <Box sx={classes.imgContainer}>
            {companyImg && (
              <img
                src={companyImg.src}
                alt={companyImg.alt}
                style={stylesHelper() && stylesHelper().cloudGaia}
              />
            )}
            {partnerImg && (
              <img
                src={partnerImg.src}
                alt={partnerImg.alt}
                style={stylesHelper() && stylesHelper().salesforcePartner}
              />
            )}
          </Box>
        </Box>
      </Box>

      {!isMobile && (
        <Box sx={classes.imageBox}>
          {backgroundLogoImg && (
            <img
              src={backgroundLogoImg.src}
              alt={backgroundLogoImg.alt}
              style={stylesHelper() && stylesHelper().mainImage}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default BootcampTitle;
